// apps/web/src/features/commercial-proposals/public/PublicProposalPage.tsx
//
// @aiPurpose route element for `/p` — the standalone, unauthenticated public
//   commercial-proposal page (#1199). The token is read from the URL
//   FRAGMENT (`#t=<token>`) so it never reaches the server in a query string.
// @aiSecurity this page mounts OUTSIDE RootLayout / ProtectedRoute / AppShell
//   (see router/routes.tsx): no auth store, no nav, no bootstrap/refresh. It
//   uses first-party tokens only (no third-party subresources) and tags the
//   document `noindex,nofollow` so an accidentally-shared link is not indexed.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { parsePublicTokenFromHash } from './publicApi.js';
import { usePublicProposal } from './usePublicProposal.js';
import { PublicProposalSheet } from './PublicProposalSheet.js';
import { PublicProposalNotFound } from './PublicProposalNotFound.js';

const ROBOTS_CONTENT = 'noindex,nofollow';
const REFERRER_CONTENT = 'no-referrer';

export function PublicProposalPage() {
  const { t } = useTranslation('commercialProposalsPublic');
  // WHY: read the fragment ONCE at mount. The page is a leaf surface with no
  // in-app navigation, so re-reading on hash change is unnecessary.
  const token =
    typeof window !== 'undefined' ? parsePublicTokenFromHash(window.location.hash) : null;
  const state = usePublicProposal(token);

  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = ROBOTS_CONTENT;
    document.head.appendChild(robots);

    // WHY: enforces the public-surface no-referrer requirement so even a
    // third-party subresource (e.g. the app's font CDN) and any outbound
    // navigation carry no referer. The token lives only in the fragment so
    // it is never in a referer regardless, but the meta makes that explicit
    // at the document level as required by the security spec for this page.
    const referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = REFERRER_CONTENT;
    document.head.appendChild(referrer);

    return () => {
      document.head.removeChild(robots);
      document.head.removeChild(referrer);
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <main
        data-testid="cp-public-loading"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-0)',
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--fz-13)',
        }}
      >
        {t('loading')}
      </main>
    );
  }

  if (state.status === 'notFound' || !token) return <PublicProposalNotFound />;
  return <PublicProposalSheet token={token} dto={state.dto} />;
}
